/**
 * VistoeraTASK-00073 — Local Publisher Agent (tenant product surface).
 *
 * Pure, framework-free state derivation for the "本地发布助手" page. Kept in
 * its own module (no React/antd imports) so the state machine itself is
 * unit-testable via a plain node-test script (see
 * `scripts/localPublisherAgentState.node-test.ts`), matching this repo's
 * existing convention of testing pure logic separately from JSX.
 *
 * IMPORTANT — this is a tenant-product presentation layer only. It derives
 * a simplified single-installation view from the existing
 * `LocalPublisherAgentOut[]` list (see `saas/api.ts` /
 * `backend/app/saas/services/local_publisher_agent_service.py`). It does
 * NOT talk to the Platform Admin Worker system (`WorkersPage.tsx`) and
 * does NOT introduce any new backend contract — `revoked` agents are
 * already excluded server-side (`list_agents` filters
 * `revoked_at IS NULL`), so a revoked/invalidated installation simply
 * disappears from this list and the page canonically falls back to
 * `not_connected` (STATE_A / STATE_F collapse — see task spec P3/P10).
 */

export type RawAgentStatus = 'online' | 'offline' | 'busy' | 'auth_required' | 'update_required' | 'revoked'

/** Minimal shape this module needs from `LocalPublisherAgentOut` — kept
 * structurally typed (not importing the antd-adjacent `api.ts` type) so
 * this file has zero framework/API-client coupling. */
export interface LocalPublisherAgentLike {
  id: string
  display_name: string
  machine_name: string | null
  os_name: string | null
  agent_version: string | null
  status: RawAgentStatus
  last_seen_at: string | null
  created_at: string | null
}

/** P10 — canonical single-installation selection policy: the backend
 * already orders `list_agents` by `created_at DESC` and excludes revoked
 * rows, so "current active installation" is simply the first (most
 * recently created, non-revoked) row. Any additional historical rows are
 * intentionally NOT surfaced on this page (not deleted — just not shown;
 * see task spec P10/P11). */
export function selectCanonicalInstallation<T extends LocalPublisherAgentLike>(agents: T[] | null): T | null {
  if (!agents || agents.length === 0) return null
  return agents[0]
}

export type LocalPublisherPageState =
  | { kind: 'loading' }
  | { kind: 'not_connected' }
  | { kind: 'connecting' }
  | { kind: 'connected'; installation: LocalPublisherAgentLike; needsAttention: boolean; busy: boolean }
  | { kind: 'update_required'; installation: LocalPublisherAgentLike }
  | { kind: 'offline'; installation: LocalPublisherAgentLike }

/**
 * Maps the raw agent list + local ephemeral pairing flag onto the six
 * product states from task spec P3 (STATE_A..F). STATE_F
 * (REVOKED_OR_PAIRING_INVALID) is deliberately not a distinct return
 * value: because revoked rows are excluded server-side, "revoked" is
 * observationally identical to "never connected" from this list — both
 * resolve to `not_connected`, whose call-to-action text is written to
 * read correctly for either a first-time or a reconnecting user.
 */
export function deriveLocalPublisherPageState(
  agents: LocalPublisherAgentLike[] | null,
  opts: { pairingActive: boolean },
): LocalPublisherPageState {
  if (agents === null) return { kind: 'loading' }

  const canonical = selectCanonicalInstallation(agents)

  // A pairing session is only "active" (STATE_B) until *any* agent shows
  // up for this workspace — once one exists, its own status (which starts
  // at `offline` immediately after enroll, before the first heartbeat)
  // takes over and drives the rest of the state machine.
  if (opts.pairingActive && canonical === null) {
    return { kind: 'connecting' }
  }

  if (canonical === null) {
    return { kind: 'not_connected' }
  }

  if (canonical.status === 'update_required') {
    return { kind: 'update_required', installation: canonical }
  }

  if (canonical.status === 'offline') {
    return { kind: 'offline', installation: canonical }
  }

  // online | busy | auth_required — agent process is reachable.
  return {
    kind: 'connected',
    installation: canonical,
    needsAttention: canonical.status === 'auth_required',
    busy: canonical.status === 'busy',
  }
}

/** Whether a pairing session (server-issued `expires_at`) is still within
 * its single-use TTL window. Pure function of two timestamps so it is
 * trivially unit-testable without faking a clock/timer. */
export function isPairingExpired(expiresAtIso: string, nowMs: number): boolean {
  const expiresMs = new Date(expiresAtIso).getTime()
  if (Number.isNaN(expiresMs)) return true
  return nowMs >= expiresMs
}

export function pairingSecondsRemaining(expiresAtIso: string, nowMs: number): number {
  const expiresMs = new Date(expiresAtIso).getTime()
  if (Number.isNaN(expiresMs)) return 0
  return Math.max(0, Math.floor((expiresMs - nowMs) / 1000))
}

/**
 * VistoeraTASK-00082 P3/P4 — OS detection for the download area. This is
 * UX-only (which button is visually primary/"推荐"): it NEVER decides
 * authorization, tenant, workspace, or credential scope, and the other
 * platform's download always stays reachable regardless of the result
 * (see `LocalPublisherAgentPage.tsx`'s "其他系统" secondary entry).
 *
 * Deliberately takes plain strings (`navigator.userAgentData?.platform`,
 * `navigator.platform`, `navigator.userAgent`) rather than the `Navigator`
 * object itself, so this stays a pure, node-testable function with zero
 * DOM coupling — see `scripts/localPublisherAgentState.node-test.ts`.
 */
export type LocalPublisherPlatform = 'macos' | 'windows' | 'unknown'

export function detectLocalPublisherPlatform(input: {
  uaDataPlatform?: string | null
  navigatorPlatform?: string | null
  userAgent?: string | null
}): LocalPublisherPlatform {
  const uaDataPlatform = (input.uaDataPlatform || '').toLowerCase()
  if (uaDataPlatform.includes('mac')) return 'macos'
  if (uaDataPlatform.includes('win')) return 'windows'

  const navigatorPlatform = (input.navigatorPlatform || '').toLowerCase()
  if (navigatorPlatform.includes('mac')) return 'macos'
  if (navigatorPlatform.includes('win')) return 'windows'

  const userAgent = (input.userAgent || '').toLowerCase()
  if (userAgent.includes('mac os') || userAgent.includes('macintosh')) return 'macos'
  if (userAgent.includes('windows')) return 'windows'

  return 'unknown'
}

/** Minimal shape this module needs from the `/api/local-publisher/releases/latest`
 * response for one platform — kept structurally typed like `LocalPublisherAgentLike`
 * above (no `api.ts` import). */
export interface LocalPublisherReleaseLike {
  platform: string
  ready: boolean
  version?: string
  size_bytes?: number
  download_url?: string
}

/** P6 absolute no-fake-download guard, expressed as a pure predicate the
 * component can trust instead of re-deriving its own "is this real"
 * logic: a platform's download is only ever actionable when the release
 * metadata API itself reports `ready === true` (see
 * `local_publisher_release_service.get_latest_release` — it never
 * fabricates a `download_url`). */
export function isReleaseDownloadReady(release: LocalPublisherReleaseLike | null | undefined): boolean {
  return Boolean(release && release.ready && release.download_url)
}

/** VistoeraTASK-00105 — explicit fetch lifecycle for release metadata;
 * must not conflate API loading, API error, and genuine unavailable into
 * one "版本准备中" label. */
export type LocalPublisherReleasesFetchState = 'loading' | 'loaded' | 'error'

/** Maps the download-button's ``kind`` argument to the canonical platform
 * key returned by ``GET /api/local-publisher/releases`` (``windows`` /
 * ``macos`` — never ``win`` / ``mac``). */
export function localPublisherReleasePlatformKey(kind: 'mac' | 'win'): 'macos' | 'windows' {
  return kind === 'mac' ? 'macos' : 'windows'
}

export type LocalPublisherDownloadUiState = 'loading' | 'ready' | 'unavailable' | 'error'

/** Pure per-platform download UI state — keeps ``LocalPublisherAgentPage``'
 * ``downloadButton`` from re-deriving loading/error/unavailable logic inline. */
export function deriveLocalPublisherDownloadUiState(
  fetchState: LocalPublisherReleasesFetchState,
  release: LocalPublisherReleaseLike | null | undefined,
): LocalPublisherDownloadUiState {
  if (fetchState === 'loading') return 'loading'
  if (fetchState === 'error') return 'error'
  return isReleaseDownloadReady(release) ? 'ready' : 'unavailable'
}

/** Builds the secondary download-button line: ``{subtitle} · v{version} · {size}``.
 * Size is omitted while loading, when unknown, or zero — never shows ``0 MB``. */
export function formatReleaseDownloadMeta(
  subtitle: string,
  version?: string,
  sizeBytes?: number,
  formatSize?: (bytes: number) => string,
): string {
  let meta = subtitle
  if (version) meta += ` · v${version}`
  if (sizeBytes != null && sizeBytes > 0 && formatSize) {
    meta += ` · ${formatSize(sizeBytes)}`
  }
  return meta
}
